import React, { useRef, useState } from 'react';
import distributorService from '../../services/distributorService';
import useShippingRequest, { shippingAccessScope } from '../../hooks/useShippingRequest';
import { captureSession, isSessionCurrent } from '../../utils/authSession';
import { useT } from '../../i18n/LanguageContext';
import { readPurchaseReceipt, samePurchaseQuote } from '../distributor/distributorPurchaseReceipt';

/** Record verified provider evidence. This never refunds a card or changes bank units. */
export default function DistributorCardChargeback({
  distributorId,
  purchase,
  disabled,
  onRecorded,
  onPendingChange,
}) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [caseReference, setCaseReference] = useState('');
  const [amount, setAmount] = useState('');
  const [observedAt, setObservedAt] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [intent, setIntent] = useState(null);
  const pending = useRef(false);
  const intentRef = useRef(null);
  const session = useRef(captureSession());
  const scope = useRef(shippingAccessScope());
  const { begin } = useShippingRequest(`${distributorId}:${purchase._id}`);
  const current = () =>
    isSessionCurrent(session.current) && scope.current === shippingAccessScope();
  const cents = /^\d{1,12}(?:\.\d{1,2})?$/.test(amount)
    ? Number(amount.split('.')[0]) * 100 + Number((amount.split('.')[1] || '').padEnd(2, '0'))
    : null;
  const day = Date.parse(`${observedAt}T00:00:00Z`);
  const valid =
    confirmed &&
    /^[A-Za-z0-9_-]{3,100}$/.test(caseReference) &&
    Number.isSafeInteger(cents) &&
    cents > 0 &&
    cents <= purchase.amountCents &&
    /^\d{4}-\d{2}-\d{2}$/.test(observedAt) &&
    Number.isFinite(day) &&
    new Date(day).toISOString().slice(0, 10) === observedAt &&
    day <= Date.now();
  const submit = async (event) => {
    event.preventDefault();
    if (!current() || pending.current || disabled) return;
    let captured = intentRef.current;
    if (!captured) {
      if (!valid || !purchase.recovery?.canRecordChargeback || !purchase.recovery.paymentReference)
        return;
      const requestKey = window.crypto?.randomUUID?.();
      if (!requestKey) {
        setError('A secure request key is unavailable. Reload before reviewing.');
        return;
      }
      captured = {
        quote: purchase,
        body: {
          requestKey,
          expectedRevision: purchase.recovery.revision,
          caseReference,
          transactionId: purchase.recovery.paymentReference,
          amountCents: cents,
          observedAt,
          confirmedVerified: true,
        },
      };
      intentRef.current = captured;
      setIntent(captured);
      onPendingChange?.(true);
    }
    pending.current = true;
    setBusy(true);
    setError('');
    const request = begin();
    try {
      const value = readPurchaseReceipt(
        await distributorService.recordCardChargeback(distributorId, purchase._id, captured.body)
      );
      if (!request.isCurrent() || !current()) return;
      const action = value.chargebackReceipt;
      if (
        value._id !== purchase._id ||
        !samePurchaseQuote(value, captured.quote) ||
        value.status !== captured.quote.status ||
        value.bankEntryId !== captured.quote.bankEntryId ||
        value.creditedAt !== captured.quote.creditedAt ||
        action?.requestKey !== captured.body.requestKey ||
        action.expectedRevision !== captured.body.expectedRevision ||
        action.recordedRevision !== captured.body.expectedRevision + 1 ||
        !Number.isFinite(Date.parse(action.recordedAt)) ||
        Date.parse(action.recordedAt) < Date.parse(purchase.createdAt) ||
        !Number.isSafeInteger(value.recovery?.revision) ||
        value.recovery.revision < action.recordedRevision ||
        typeof value.recovery.blocked !== 'boolean' ||
        (value.recovery.revision === action.recordedRevision && !value.recovery.blocked)
      )
        throw new Error('Invalid chargeback receipt');
      intentRef.current = null;
      setIntent(null);
      onPendingChange?.(false);
      setOpen(false);
      onRecorded?.();
    } catch {
      if (request.isCurrent() && current())
        setError(
          'Chargeback result is unconfirmed. Recover the same request before recording another.'
        );
    } finally {
      if (request.isCurrent() && current()) {
        pending.current = false;
        setBusy(false);
      }
    }
  };
  if (!open)
    return (
      <button
        type="button"
        className="me-dw-link mr-3"
        disabled={disabled}
        onClick={() => {
          if (current()) setOpen(true);
        }}
      >
        {t('Record verified chargeback')}
      </button>
    );
  return (
    <form
      onSubmit={submit}
      className="space-y-3 border-t pt-3"
      aria-label={t('Verified card chargeback')}
    >
      <p>
        {t(
          'Verify the provider case against this payment reference. Do not paste a notice or card details. No refund is initiated and no units are deducted.'
        )}
      </p>
      <p>
        {t('Payment reference')}: {purchase.recovery.paymentReference}
      </p>
      {error && <p role="alert">{t(error)}</p>}
      <fieldset disabled={busy || disabled || Boolean(intent)} className="space-y-2">
        <label className="block">
          {t('Provider case reference')}
          <input
            className="block rounded border p-2"
            maxLength={100}
            value={caseReference}
            onChange={(event) => {
              setCaseReference(event.target.value);
              setConfirmed(false);
            }}
          />
        </label>
        <label className="block">
          {t('Chargeback amount (USD)')}
          <input
            className="block rounded border p-2"
            inputMode="decimal"
            value={amount}
            onChange={(event) => {
              setAmount(event.target.value);
              setConfirmed(false);
            }}
          />
        </label>
        <label className="block">
          {t('Provider notice date')}
          <input
            type="date"
            className="block rounded border p-2"
            value={observedAt}
            onChange={(event) => {
              setObservedAt(event.target.value);
              setConfirmed(false);
            }}
          />
        </label>
        <label className="flex gap-2">
          <input
            type="checkbox"
            checked={confirmed}
            onChange={(event) => setConfirmed(event.target.checked)}
          />
          {t(
            'I verified the chargeback case, payment reference, amount and date in the provider records.'
          )}
        </label>
      </fieldset>
      <div className="flex flex-wrap items-center gap-3">
        <button
          className="me-dw-primary"
          type="submit"
          disabled={busy || disabled || (!intent && !valid)}
        >
          {t(intent ? 'Recover same chargeback request' : 'Record verified chargeback')}
        </button>
        {!intent && (
          <button
            className="me-dw-link"
            type="button"
            disabled={busy}
            onClick={() => setOpen(false)}
          >
            {t('Cancel')}
          </button>
        )}
      </div>
    </form>
  );
}
